import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import HarvestForm from '../components/harvesting/HarvestForm';
import HarvestMap  from '../components/harvesting/HarvestMap';
import MapEngine   from '../components/harvesting/MapEngine';
import { DashTile, DashSection, DashSegBar, DB_STYLE, downloadFile, FilterChipBar } from '../components/dashboard/DashKit';
import { useUrlParams } from '../hooks/useUrlParams';
import {
  getStats,
  getRecords,
  getMyRecords,
  submitRecord,
  reviewRecord,
  deleteRecord,
} from '../services/harvestingService';

// ─── Shared styles ────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1100, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 20 },
  tabs:    { display: 'flex', gap: 8, marginBottom: 24 },
  tab: (active) => ({
    padding: '8px 18px', borderRadius: 6, fontWeight: 600, fontSize: 14,
    border: active ? '2px solid #166534' : '2px solid #e5e7eb',
    background: active ? '#f0fdf4' : '#fff',
    color: active ? '#166534' : '#6b7280',
    cursor: 'pointer',
  }),
  card: {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 10, padding: '18px 20px', marginBottom: 14,
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px',
    borderRadius: 10, fontSize: 11, fontWeight: 700,
    background: color + '22', color: color,
  }),
  btn: (bg = '#1d4ed8', fg = '#fff') => ({
    padding: '7px 16px', background: bg, color: fg,
    border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  }),
  err: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  stat: {
    background: '#f9fafb', borderRadius: 8, padding: '14px 18px',
    border: '1px solid #e5e7eb', textAlign: 'center',
  },
};

const STATUS_COLOR = { submitted: '#f59e0b', approved: '#22c55e', rejected: '#ef4444', draft: '#9ca3af' };
const CATEGORY_LABEL = {
  plants_medicine: '🌿 Plants & Medicine',
  hunting:         '🦌 Hunting',
  birds:           '🦅 Birds',
  fishing:         '🎣 Fishing',
  environmental:   '⚠️ Environmental',
  other:           '📍 Other',
};
const SEASON_LABEL = { spring: '🌸 Spring', summer: '☀️ Summer', fall: '🍂 Fall', winter: '❄️ Winter' };

// ─── Helpers ─────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  return (
    <span style={S.badge(STATUS_COLOR[status] || '#9ca3af')}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function StatGrid({ items, keyLabel, countLabel = 'Count' }) {
  if (!items?.length) return <p style={{ color: '#6b7280' }}>No data yet.</p>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
      {items.map((i) => (
        <div key={i._id} style={S.stat}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{i.count}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {CATEGORY_LABEL[i._id] || SEASON_LABEL[i._id] || i._id || '(unknown)'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── RecordCard ───────────────────────────────────────────────────────────────
function RecordCard({ record, onReview, onDelete, canReview, isOwn }) {
  const [expanded, setExpanded] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function submitReview() {
    setSaving(true);
    try {
      await onReview(record._id, { status: reviewStatus, reviewNotes });
      setReviewOpen(false);
    } finally {
      setSaving(false);
    }
  }

  const lat = record.location?.coordinates?.[1];
  const lng = record.location?.coordinates?.[0];
  const hasCoords = lat != null && lng != null;

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {CATEGORY_LABEL[record.category] || record.category}
          </span>
          {record.subcategory && (
            <span style={{ color: '#6b7280', marginLeft: 8 }}>— {record.subcategory}</span>
          )}
          {record.species && (
            <span style={{ color: '#374151', marginLeft: 8, fontStyle: 'italic' }}>{record.species}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isOwn && record.status === 'submitted'
            ? <span style={S.badge('#22c55e')}>✓ Saved</span>
            : <StatusBadge status={record.status} />}
          <button style={S.btn('#f3f4f6', '#374151')} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▲ Less' : '▼ More'}
          </button>
          {canReview && record.status === 'submitted' && (
            <button style={S.btn()} onClick={() => setReviewOpen(!reviewOpen)}>
              Review
            </button>
          )}
          {(isOwn || canReview) && ['draft', 'submitted'].includes(record.status) && (
            <button
              style={S.btn('#ef4444')}
              onClick={() => { if (window.confirm('Delete this record?')) onDelete(record._id); }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>{SEASON_LABEL[record.season] || record.season}</span>
        <span>📅 {new Date(record.harvestDate).toLocaleDateString()}</span>
        {record.quantity && <span>Qty: {record.quantity} {record.unit}</span>}
        {record.abundance && <span>Abundance: {record.abundance.replace('_', ' ')}</span>}
        {hasCoords && <span>📍 {lat.toFixed(4)}, {lng.toFixed(4)}</span>}
        {record.userId?.name && <span>By: {record.userId.name}</span>}
      </div>

      {expanded && (
        <div style={{ marginTop: 12, fontSize: 14, color: '#374151' }}>
          {record.observations && (
            <p style={{ marginBottom: 8 }}>
              <strong>Observations:</strong> {record.observations}
            </p>
          )}
          {record.traditionalKnowledge && (
            <p style={{ marginBottom: 8 }}>
              <strong>Traditional knowledge:</strong> {record.traditionalKnowledge}
            </p>
          )}
          {record.isTraditionalUse && (
            <p style={{ color: '#166534' }}>
              ✓ Marked as traditional use site
            </p>
          )}
          {record.locationDescription && (
            <p style={{ marginBottom: 8 }}>
              <strong>Location notes:</strong> {record.locationDescription}
            </p>
          )}
          {record.reviewNotes && (
            <p style={{ background: '#fefce8', padding: '8px 12px', borderRadius: 6, marginTop: 8 }}>
              <strong>Review notes:</strong> {record.reviewNotes}
            </p>
          )}
          {record.reviewedBy?.name && (
            <p style={{ color: '#6b7280', fontSize: 12 }}>
              Reviewed by {record.reviewedBy.name} on{' '}
              {record.reviewedAt ? new Date(record.reviewedAt).toLocaleDateString() : '—'}
            </p>
          )}
        </div>
      )}

      {reviewOpen && (
        <div
          style={{
            marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: '14px 16px',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 10 }}>Review this record</div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            {['approved', 'rejected'].map((s) => (
              <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  value={s}
                  checked={reviewStatus === s}
                  onChange={() => setReviewStatus(s)}
                />
                <StatusBadge status={s} />
              </label>
            ))}
          </div>
          <textarea
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder="Optional notes for the contributor…"
            rows={2}
            style={{
              width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
              borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button style={S.btn(reviewStatus === 'approved' ? '#16a34a' : '#ef4444')} onClick={submitReview} disabled={saving}>
              {saving ? 'Saving…' : `Confirm ${reviewStatus}`}
            </button>
            <button style={S.btn('#f3f4f6', '#374151')} onClick={() => setReviewOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ReviewerView ─────────────────────────────────────────────────────────────
function ReviewerView({ tenantId }) {
  const [urlP, setUrlP] = useUrlParams({ tab: 'pending', category: '', dateFrom: '', dateTo: '' });
  const tab            = ['pending', 'approved', 'map', 'stats', 'dashboard'].includes(urlP.tab) ? urlP.tab : 'pending';
  const categoryFilter = urlP.category;
  const dashDateFrom   = urlP.dateFrom;
  const dashDateTo     = urlP.dateTo;
  const setTab             = (v) => setUrlP('tab',      v);
  const setCategoryFilter  = (v) => setUrlP('category', v);
  const setDashFrom        = (v) => setUrlP('dateFrom', v);
  const setDashTo          = (v) => setUrlP('dateTo',   v);
  const [stats, setStats]           = useState(null);
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, recRes] = await Promise.all([
        getStats(tenantId),
        getRecords(tenantId, { limit: 100 }),
      ]);
      setStats(statsRes.data);
      setRecords(recRes.data.records);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load harvesting data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleReview = async (id, data) => {
    await reviewRecord(tenantId, id, data);
    await loadAll();
  };

  const handleDelete = async (id) => {
    await deleteRecord(tenantId, id);
    await loadAll();
  };

  const pending  = records.filter((r) => r.status === 'submitted').filter((r) => !categoryFilter || r.category === categoryFilter);
  const approved = records.filter((r) => r.status === 'approved').filter((r) => !categoryFilter || r.category === categoryFilter);
  const all      = categoryFilter ? records.filter((r) => r.category === categoryFilter) : records;

  if (loading) return <p>Loading…</p>;
  if (error)   return <div style={S.err}>{error}</div>;

  return (
    <div>
      {/* ── Quick stats summary ── */}
      {stats && (
        <div
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))',
            gap: 10, marginBottom: 24,
          }}
        >
          {stats.byStatus.map((s) => (
            <div key={s._id} style={S.stat}>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{s.count}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                <StatusBadge status={s._id} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Drill-through chips: tab view + category filter ── */}
      {(tab !== 'pending' || categoryFilter) && (() => {
        const TAB_META = {
          approved:  { label: 'Approved',  color: '#22c55e' },
          map:       { label: 'Map View',  color: '#0ea5e9' },
          stats:     { label: 'Stats',     color: '#a855f7' },
          dashboard: { label: 'Dashboard', color: '#0ea5e9' },
        };
        const chips = [];
        if (tab !== 'pending') {
          const meta = TAB_META[tab];
          if (meta) chips.push({ label: `View: ${meta.label}`, color: meta.color, onRemove: () => setTab('pending') });
        }
        if (categoryFilter) {
          chips.push({ label: `Cat: ${CATEGORY_LABEL[categoryFilter] || categoryFilter}`, color: '#16a34a', onRemove: () => setCategoryFilter('') });
        }
        if (!chips.length) return null;
        return (
          <FilterChipBar
            chips={chips}
            hint={tab !== 'pending' ? '× to return to Pending' : undefined}
            onClearAll={chips.length > 1 ? () => { setTab('pending'); setCategoryFilter(''); } : undefined}
          />
        );
      })()}

      {/* ── Tabs ── */}
      <div style={S.tabs}>
        {[
          { key: 'pending',   label: `Pending (${pending.length})`   },
          { key: 'approved',  label: `Approved (${approved.length})` },
          { key: 'map',       label: '🗺️ Map View' },
          { key: 'stats',     label: '📊 Stats' },
          { key: 'dashboard', label: '📋 Dashboard' },
        ].map((t) => (
          <button key={t.key} style={S.tab(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Pending records ── */}
      {tab === 'pending' && (
        pending.length === 0
          ? <p style={{ color: '#6b7280' }}>No pending records — all caught up! 🎉</p>
          : pending.map((r) => (
              <RecordCard
                key={r._id}
                record={r}
                canReview
                onReview={handleReview}
                onDelete={handleDelete}
              />
            ))
      )}

      {/* ── Approved records ── */}
      {tab === 'approved' && (
        approved.length === 0
          ? <p style={{ color: '#6b7280' }}>No approved records yet.</p>
          : approved.map((r) => <RecordCard key={r._id} record={r} onDelete={handleDelete} canReview />)
      )}

      {/* ── Map view ── */}
      {tab === 'map' && (() => {
        const dataLayers = Object.entries(
          all.reduce((acc, r) => {
            if (!r.location?.coordinates) return acc;
            (acc[r.category] = acc[r.category] || []).push(r);
            return acc;
          }, {})
        ).map(([cat, data]) => ({ dataTypeId: cat, data }));
        return dataLayers.length > 0
          ? <MapEngine dataLayers={dataLayers} height="540px" onFeatureClick={null} />
          : <p style={{ color: '#6b7280' }}>No geotagged records to display on the map.</p>;
      })()}

      {/* ── Stats view ── */}
      {tab === 'stats' && stats && (
        <div>
          <h3 style={{ fontWeight: 700, marginBottom: 12 }}>By Category</h3>
          <StatGrid items={stats.byCategory} />
          <h3 style={{ fontWeight: 700, margin: '20px 0 12px' }}>By Season</h3>
          <StatGrid items={stats.bySeason} />
          <h3 style={{ fontWeight: 700, margin: '20px 0 12px' }}>Recent submissions</h3>
          {stats.recentRecords?.map((r) => (
            <div key={r._id} style={{ ...S.card, fontSize: 14 }}>
              {CATEGORY_LABEL[r.category] || r.category}
              {r.species ? ` — ${r.species}` : ''}
              <span style={{ color: '#6b7280', marginLeft: 8 }}>
                by {r.userId?.name || 'Unknown'} · {new Date(r.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Dashboard view ── */}
      {tab === 'dashboard' && (() => {
        // Apply date filter to all records using harvestDate
        let dash = [...records];
        if (dashDateFrom) {
          const from = new Date(dashDateFrom);
          dash = dash.filter(r => new Date(r.harvestDate) >= from);
        }
        if (dashDateTo) {
          const to = new Date(dashDateTo);
          to.setHours(23, 59, 59, 999);
          dash = dash.filter(r => new Date(r.harvestDate) <= to);
        }
        const hasFilter = !!(dashDateFrom || dashDateTo);

        const dTotal    = dash.length;
        const dPending  = dash.filter(r => r.status === 'submitted').length;
        const dApproved = dash.filter(r => r.status === 'approved').length;
        const dRejected = dash.filter(r => r.status === 'rejected').length;

        // Category breakdown
        const catKeys = Object.keys(CATEGORY_LABEL);
        const catTally = catKeys.map(k => ({
          key: k, label: CATEGORY_LABEL[k].replace(/^.*?\s/, ''),
          count: dash.filter(r => r.category === k).length,
        })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);

        // Season breakdown
        const seasonColors = { spring:'#22c55e', summer:'#f59e0b', fall:'#f97316', winter:'#0ea5e9' };
        const seasonTally = Object.entries(SEASON_LABEL).map(([k, label]) => ({
          key: k, label: label.replace(/^.*?\s/, ''), color: seasonColors[k] || '#64748b',
          count: dash.filter(r => r.season === k).length,
        })).filter(s => s.count > 0);

        // Oldest unreviewed (attention queue)
        const attentionQ = [...dash.filter(r => r.status === 'submitted')]
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .slice(0, 8);

        // Exports
        const buildHarvestMD = () => {
          const now = new Date();
          const lines = [`# Harvest Records — Staff Summary`,'',
            `**Generated:** ${now.toLocaleDateString('en-GB')} ${now.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`,
            `**Date filter:** ${(dashDateFrom||dashDateTo) ? `${dashDateFrom||'start'} → ${dashDateTo||'now'}` : 'All dates'}`,
            `**Records in view:** ${dTotal}`,
            '', '---', '',
            '## Overview', '',
            '| Status | Count |', '|--------|-------|',
            `| Pending Review | ${dPending} |`,
            `| Approved | ${dApproved} |`,
            `| Rejected | ${dRejected} |`,
            '', '---', '', '## By Category', '',
            '| Category | Count |', '|----------|-------|',
            ...catTally.map(c => `| ${CATEGORY_LABEL[c.key] || c.label} | ${c.count} |`),
            '', '---', '', '## By Season', '',
            '| Season | Count |', '|--------|-------|',
            ...seasonTally.map(s => `| ${SEASON_LABEL[s.key] || s.label} | ${s.count} |`),
          ];
          if (attentionQ.length) {
            lines.push('', '---', '', `## Oldest Pending (${attentionQ.length})`, '');
            attentionQ.forEach(r => {
              lines.push(`- ${CATEGORY_LABEL[r.category] || r.category}${r.species ? ` — ${r.species}` : ''} · harvested ${new Date(r.harvestDate).toLocaleDateString()}`);
            });
          }
          lines.push('', '---', '', '_CIP harvesting staff export._');
          return lines.join('\n');
        };

        const buildHarvestCSV = () => {
          const esc = v => `"${String(v??'').replace(/"/g,'""')}"`;
          const hdr = ['Category','Species','Season','Harvest Date','Status','Contributor','Submitted','Observations'];
          const rows = dash.map(r => [
            esc(CATEGORY_LABEL[r.category] || r.category),
            esc(r.species || ''),
            esc(SEASON_LABEL[r.season] || r.season || ''),
            esc(r.harvestDate ? new Date(r.harvestDate).toLocaleDateString('en-GB') : ''),
            esc(r.status),
            esc(r.userId?.name || ''),
            esc(r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : ''),
            esc(r.observations || ''),
          ].join(','));
          return [hdr.join(','), ...rows].join('\r\n');
        };

        return (
          <div style={{ fontSize:13, color:'#e2e8f0' }}>
            {/* Toolbar */}
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:14 }}>
              <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>FROM</span>
              <input type="date" value={dashDateFrom} onChange={e => setDashFrom(e.target.value)}
                style={DB_STYLE.dateInput} />
              <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>TO</span>
              <input type="date" value={dashDateTo} onChange={e => setDashTo(e.target.value)}
                style={DB_STYLE.dateInput} />
              <div style={{ flex:1 }} />
              <button onClick={() => downloadFile(buildHarvestMD(), 'harvest-summary.md', 'text/markdown;charset=utf-8')}
                style={DB_STYLE.btnMd}>
                📥 Export .md
              </button>
              <button onClick={() => downloadFile(buildHarvestCSV(), 'harvest-records.csv', 'text/csv;charset=utf-8')}
                style={DB_STYLE.btnCsv}>
                📊 Export .csv
              </button>
            </div>

            {/* Date filter chips */}
            {hasFilter && (
              <FilterChipBar
                chips={[
                  dashDateFrom && { label: `From: ${dashDateFrom}`, color: '#0ea5e9', onRemove: () => setDashFrom('') },
                  dashDateTo   && { label: `To: ${dashDateTo}`,     color: '#0ea5e9', onRemove: () => setDashTo('') },
                ].filter(Boolean)}
                onClearAll={dashDateFrom && dashDateTo ? () => { setDashFrom(''); setDashTo(''); } : undefined}
              />
            )}

            {/* Stat tiles */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:4 }}>
              <DashTile label={`Records${hasFilter ? ' (filtered)' : ''}`} value={dTotal} color="#64748b" />
              <DashTile label="Pending Review" value={dPending} color={dPending > 0 ? '#f59e0b' : '#334155'} />
              <DashTile label="Approved" value={dApproved} color="#22c55e" />
              <DashTile label="Rejected" value={dRejected} color={dRejected > 0 ? '#ef4444' : '#334155'} />
            </div>

            {/* Category breakdown */}
            <DashSection label="BY CATEGORY" />
            {catTally.length > 0 ? <DashSegBar items={catTally} total={dTotal} />
              : <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>No records.</div>}

            {/* Season breakdown */}
            <DashSection label="BY SEASON" />
            {seasonTally.length > 0 ? <DashSegBar items={seasonTally} total={dTotal} />
              : <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>No records.</div>}

            {/* Oldest pending (attention queue) */}
            {attentionQ.length > 0 && (
              <>
                <DashSection label={`OLDEST PENDING REVIEW (${attentionQ.length})`} />
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  {attentionQ.map(r => (
                    <div key={r._id} style={{ background:'#0f172a', borderRadius:6, padding:'8px 12px',
                      borderLeft:'3px solid #f59e0b', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, color:'#cbd5e1', flex:1 }}>
                        {CATEGORY_LABEL[r.category] || r.category}
                        {r.species ? ` — ${r.species}` : ''}
                      </span>
                      <span style={{ fontSize:10, color:'#64748b' }}>
                        harvested {new Date(r.harvestDate).toLocaleDateString()}
                      </span>
                      <span style={{ fontSize:10, color:'#475569' }}>
                        by {r.userId?.name || 'unknown'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {attentionQ.length === 0 && (
              <div style={{ marginTop:14, fontSize:13, color:'#22c55e', fontStyle:'italic' }}>
                ✓ No pending records in the selected period.
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── ContributorView ──────────────────────────────────────────────────────────
function ContributorView({ tenantId }) {
  const [tab, setTab]         = useState('submit');
  const [logIntent, setLogIntent] = useState(null); // null | 'harvest' | 'count' | 'environmental'
  const [myRecords, setMyRec] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubm] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const loadMyRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMyRecords(tenantId, { limit: 100 });
      setMyRec(res.data.records);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load your records');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tab === 'history' || tab === 'map') loadMyRecords();
  }, [tab, loadMyRecords]);

  const handleSubmit = async (formData) => {
    setSubm(true);
    setError('');
    setSuccess('');
    try {
      await submitRecord(tenantId, formData);
      setSuccess('Your harvest record has been submitted. Thank you for contributing!');
      setTab('history');
      setLogIntent(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubm(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteRecord(tenantId, id);
    await loadMyRecords();
  };

  const mappedRecords = myRecords.filter((r) => r.location?.coordinates?.length === 2);

  return (
    <div>
      <div style={S.tabs}>
        {[
          { key: 'submit',  label: '📝 Log Harvest' },
          { key: 'history', label: `My Records (${myRecords.length || '…'})` },
          { key: 'map',     label: '🗺️ My Map' },
        ].map((t) => (
          <button key={t.key} style={S.tab(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Log Harvest ── */}
      {tab === 'submit' && (
        <div>
          {success && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 6, padding: '12px 16px', color: '#166534',
              fontSize: 14, marginBottom: 16,
            }}>
              ✅ {success}
            </div>
          )}
          {!logIntent && (
            <div>
              <p style={{ fontSize: 15, color: '#374151', marginBottom: 16 }}>
                What would you like to record today?
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 10 }}>
                {[
                  { key: 'harvest', icon: '🌿', title: 'Log a Harvest', desc: 'Record fish, game, berries, plants, or anything you harvested.', color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
                  { key: 'count', icon: '📊', title: 'Species Count', desc: 'Count or report what you observed. Every sighting helps track population health.', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
                  { key: 'environmental', icon: '⚠️', title: 'Environmental Report', desc: 'Report pollution, habitat damage, water quality issues, or other concerns.', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setLogIntent(opt.key)}
                    style={{ background: opt.bg, border: `2px solid ${opt.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'left', cursor: 'pointer', transition: 'box-shadow .15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.10)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ fontSize: 26, marginBottom: 6 }}>{opt.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: opt.color, marginBottom: 4 }}>{opt.title}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                All submissions are yours — you can edit or remove them at any time.
              </p>
            </div>
          )}
          {logIntent && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: logIntent === 'environmental' ? '#fffbeb' : logIntent === 'count' ? '#eff6ff' : '#f0fdf4', border: `1px solid ${logIntent === 'environmental' ? '#fde68a' : logIntent === 'count' ? '#bfdbfe' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                  {logIntent === 'harvest' && '🌿 Logging a Harvest'}
                  {logIntent === 'count' && '📊 Species Count / Observation'}
                  {logIntent === 'environmental' && '⚠️ Environmental Report'}
                </span>
                <button type="button" onClick={() => setLogIntent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280', textDecoration: 'underline', padding: 0 }}>
                  ← Change
                </button>
              </div>
              <HarvestForm
                onSubmit={handleSubmit}
                loading={submitting}
                error={error}
                initial={logIntent === 'environmental' ? { category: 'environmental' } : {}}
              />
            </div>
          )}
        </div>
      )}

      {/* ── My Records history ── */}
      {tab === 'history' && (
        loading
          ? <p>Loading…</p>
          : myRecords.length === 0
            ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
                <p style={{ fontSize: 32 }}>🌿</p>
                <p>No records yet. Start by submitting your first harvest record!</p>
                <button style={{ ...S.btn('#16a34a'), marginTop: 8 }} onClick={() => setTab('submit')}>
                  Submit a Record
                </button>
              </div>
            )
            : myRecords.map((r) => (
                <RecordCard
                  key={r._id}
                  record={r}
                  isOwn
                  onDelete={handleDelete}
                />
              ))
      )}

      {/* ── My Map ── */}
      {tab === 'map' && (
        loading
          ? <p>Loading…</p>
          : mappedRecords.length === 0
            ? <p style={{ color: '#6b7280' }}>None of your records have coordinates yet.</p>
            : <HarvestMap records={mappedRecords} readonly />
      )}
    </div>
  );
}

// ─── HarvestingPage ───────────────────────────────────────────────────────────
// mode prop: 'contribute' | 'review' | 'oversight' | undefined (auto-derived)
export default function HarvestingPage({ mode }) {
  const { activeTenant, activeMembership, user, panel } = useAuth();
  const tenantCtx = useTenantSafe();
  const location = useLocation();

  const qs = new URLSearchParams(location.search);
  const rsminReturnUrl = qs.get('rsminReturn');
  const rsminReturnLabel = qs.get('rsminLabel') || 'Back to RSMin';

  // If rendered inside TenantShell (/t/:slug/...) use the URL-derived tenant,
  // otherwise fall back to the session's single active tenant.
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;
  const communityRole = activeMembership?.communityRole;

  const forceContribute = qs.get('mode') === 'contribute';

  // community_staff reviewer access can be restricted per-tenant by a community admin
  const harvestStaffReview = tenantCtx?.viewTenant?.moduleConfig?.harvesting?.staffReviewAccess ?? true;

  const isReviewer =
    !forceContribute && (
      mode === 'review' || mode === 'oversight' ||
      communityRole === 'community_admin' ||
      (communityRole === 'community_staff' && harvestStaffReview) ||
      panel === 'admin' || panel === 'staff' ||
      ['cip_admin', 'cip_staff'].includes(user?.globalRole)
    );

  // Reviewer-role users can toggle to their own contributor view
  const [showContributor, setShowContributor] = useState(false);
  const activeView = isReviewer && showContributor ? 'contributor' : isReviewer ? 'reviewer' : 'contributor';

  if (!tenantId && !isReviewer) {
    return (
      <div style={S.page}>
        <p style={{ color: '#6b7280' }}>No active community selected.</p>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {rsminReturnUrl && (
        <div
          style={{
            marginBottom: 14,
            background: '#ecfeff',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '10px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: '#0f766e', fontSize: 13 }}>
            You are viewing CIP harvesting from RSMin.
          </span>
          <a href={rsminReturnUrl} style={{ color: '#0f766e', fontWeight: 700, textDecoration: 'none' }}>
            {rsminReturnLabel}
          </a>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h1 style={{ ...S.heading, margin: 0 }}>🌿 Harvesting</h1>
        {isReviewer && (
          activeView === 'reviewer'
            ? (
              <button
                onClick={() => setShowContributor(true)}
                style={{
                  padding: '7px 14px', background: '#f0fdf4', color: '#166534',
                  border: '1.5px solid #86efac', borderRadius: 7, fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                📝 Log My Own Activity
              </button>
            )
            : (
              <button
                onClick={() => setShowContributor(false)}
                style={{
                  padding: '7px 14px', background: '#f8fafc', color: '#374151',
                  border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                ← Review Dashboard
              </button>
            )
        )}
      </div>

      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 15 }}>
        {activeView === 'reviewer'
          ? 'Review community harvest submissions, view spatial data, and monitor seasonal trends.'
          : 'Record and track your harvesting observations. Your contributions support community planning and traditional knowledge preservation.'}
      </p>

      {activeView === 'reviewer'
        ? <ReviewerView tenantId={tenantId} />
        : <ContributorView tenantId={tenantId} />
      }
    </div>
  );
}
